import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { BoardDetailMenu } from '@/components/Board/BoardDetail/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { OvertimeTable, Section } from './styles';
import { SubContentProps } from '../LanguageSetting';
import useSWR from 'swr';
import { getNestedDataFetcher } from '@/utils/apis';
import { fetchApi } from '@/utils/api/customAxios';
import { getApprovalDocUrl, getIntranetDocUrl } from '@/utils/urls';
import { useDialog } from '@/hooks/global-tools';
import { useSnackbar } from '@/hooks/global-tools';
import { AxiosError } from 'axios';
import { PostMessageActionType, sendPostMessage } from '@/utils/postMessage';
import openCancelApprovalCreate from '@/utils/approval/openCancelApprovalCreate';
import OvertimeAdjustModal from './OvertimeAdjustModal';
import OvertimeRequestModal from './OvertimeRequestModal';
import { useTranslation } from 'react-i18next';

/** 취소 가능 유형 (attendance/histories 응답의 cancelType) */
type OvertimeCancelType = 'immediate' | 'approval' | 'none';

type OvertimeHistoryItem = {
  recordType: string;
  typeLabel: string;
  date: string;
  expiringAt: string;
  hours: number;
  approvalId: string;
  cancelType: OvertimeCancelType;
};

type OvertimeHistory = {
  remainingHours: number;
  expiringHours: number;
  histories: OvertimeHistoryItem[];
};

const OVERTIME_INITIAL_SHOW = 5;

/**
 * recordType 별 취소 설정
 * - deletePath : 즉시취소(immediate) DELETE 리소스 경로 (`/api/v1/passport/{deletePath}/{approvalId}`)
 * - apvType    : 취소 신청 품의(approval) Approval 양식 apv_type
 */
const CANCEL_CONFIG: Record<string, { deletePath: string; apvType: number }> = {
  OVERTIME: { deletePath: 'overtime', apvType: 226 },
  TOIL_TIME_ADJUSTMENT: { deletePath: 'toil-time-adjustment', apvType: 227 },
};

const Overtime = ({ myInfo }: SubContentProps) => {
  const { t } = useTranslation();

  // 'YYYY-MM-DD...' → 로케일별 'MM월 DD일' 포맷 (월/일 라벨은 번역 키로 처리)
  const formatMonthDay = (dateStr: string) => {
    const datePart = dateStr.split('T')[0];
    const [, m, d] = datePart.split('-');
    return t('MYPAGE.{{month}}월 {{day}}일', {
      month: String(Number(m)).padStart(2, '0'),
      day: String(Number(d)).padStart(2, '0'),
    });
  };

  const [isOvertimeExpanded, setIsOvertimeExpanded] = useState(false);
  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [overtimeMenuAnchor, setOvertimeMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [overtimeMenuItem, setOvertimeMenuItem] =
    useState<OvertimeHistoryItem | null>(null);
  const dialog = useDialog();
  const { showSuccess } = useSnackbar();

  const handleApprovalNavigation = (url: string) => {
    if (window?.isRNWebview) {
      sendPostMessage(PostMessageActionType.MOVE_TAB, {
        tabName: 'approval',
        url,
      });
    } else {
      window.open(url, '_blank', 'noreferrer');
    }
  };

  const { data: overTimeHistoryData, mutate: mutateOverTimeHistory } =
    useSWR<OvertimeHistory>(
      `/api/v1/passport/me/attendance/histories`,
      getNestedDataFetcher,
    );

  const handleOvertimeKebabOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    item: OvertimeHistoryItem,
  ) => {
    setOvertimeMenuAnchor(event.currentTarget);
    setOvertimeMenuItem(item);
  };

  const handleOvertimeKebabClose = () => {
    setOvertimeMenuAnchor(null);
    setOvertimeMenuItem(null);
  };

  // 즉시 취소: 결재 없이 바로 DELETE 처리 (recordType 별 리소스 경로)
  const executeImmediateCancel = async (item: OvertimeHistoryItem) => {
    const config = CANCEL_CONFIG[item.recordType];
    if (!config) return;
    try {
      await fetchApi({
        method: 'DELETE',
        url: `/api/v1/passport/${config.deletePath}/${item.approvalId}`,
      });
      mutateOverTimeHistory();
      showSuccess({ message: t('MYPAGE.취소가 완료되었습니다.') });
    } catch (e) {
      const status = e instanceof AxiosError ? e.response?.status : null;
      dialog.show({
        type: 'alert',
        message:
          status === 422
            ? t('MYPAGE.이미 처리된 신청건은 취소가 불가능합니다.')
            : t('MYPAGE.취소 처리 중 오류가 발생했습니다.'),
      });
    }
  };

  // 취소 신청 품의: Approval 취소 신청 품의 화면으로 새 탭 이동 (recordType 별 apv_type, V2/레거시 분기는 헬퍼)
  const moveToCancelApproval = (item: OvertimeHistoryItem) => {
    const config = CANCEL_CONFIG[item.recordType];
    if (!config) return;
    openCancelApprovalCreate(config.apvType, item.approvalId);
  };

  // cancelType 분기: immediate=즉시 DELETE / approval=취소품의 새 탭 / none=취소 불가
  const handleOvertimeCancelRequest = () => {
    const item = overtimeMenuItem;
    handleOvertimeKebabClose();
    if (!item?.approvalId || item.cancelType === 'none') return;
    if (item.cancelType === 'immediate') {
      // 즉시 삭제 건도 바로 삭제하지 않고 재확인 Alert 후 진행
      dialog.show({
        type: 'confirm',
        message:
          item.recordType === 'OVERTIME'
            ? t('MYPAGE.연장근무 적립 신청을 취소하시겠습니까?')
            : t('MYPAGE.연장근무 적립 사용 신청을 취소하시겠습니까?'),
        confirmButtonText: t('MYPAGE.취소하기'),
        cancelButtonText: t('MYPAGE.유지하기'),
        onConfirm: () => void executeImmediateCancel(item),
      });
      return;
    }
    // Approval 취소: 지난 근태 내역은 결재 후 취소되므로 안내 문구/버튼을 별도 노출
    dialog.show({
      type: 'confirm',
      message: (
        <>
          {t('MYPAGE.지난 근태 내역은 결재 후 취소됩니다.')}
          <br />
          {t('MYPAGE.취소 신청 품의를 작성하시겠습니까?')}
        </>
      ),
      confirmButtonText: t('MYPAGE.취소 품의 작성'),
      cancelButtonText: t('MYPAGE.닫기'),
      onConfirm: () => moveToCancelApproval(item),
    });
  };

  const histories = [...(overTimeHistoryData?.histories ?? [])].sort((a, b) =>
    b.date.localeCompare(a.date),
  );
  const visibleHistories = isOvertimeExpanded
    ? histories
    : histories.slice(0, OVERTIME_INITIAL_SHOW);
  const hasMore = histories.length > OVERTIME_INITIAL_SHOW;

  return (
    <>
      {/* 연장 근무 시간 내역 */}
      <Section sx={{ padding: '32px 20px 0' }}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            mb: '16px',
            alignItems: 'center',
          }}
        >
          <Stack flex={1} gap={'4px'}>
            <Typography
              sx={{
                color: 'var(--dk-text, #000)',
                fontSize: '16px',
                fontWeight: 700,
                lineHeight: '24px',
              }}
            >
              {t('MYPAGE.연장근무 내역')}
            </Typography>
            <Typography
              sx={{
                color: 'var(--dk-text-secondary, #888D96)',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '18px',
              }}
            >
              {t('MYPAGE.연장근무 발생일 기준 3개월 후 자동 소멸됩니다.')}
            </Typography>
          </Stack>
        </Box>

        {/* 요약 카드 */}
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            mb: '16px',
            borderTop: '1px solid var(--dk-line, #E7E9EC)',
            borderBottom: '1px solid var(--dk-line, #E7E9EC)',
          }}
        >
          <Box
            sx={{
              flex: '1 0 0',
              padding: '12px 4px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <Typography
              sx={{
                color: 'var(--dk-text-secondary, #888D96)',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
                textAlign: 'center',
              }}
            >
              {t('MYPAGE.사용 가능')}
            </Typography>
            <Typography
              sx={{
                color: 'var(--dk-text, #000)',
                fontSize: '16px',
                fontWeight: 500,
                lineHeight: '24px',
              }}
            >
              {t('MYPAGE.{{hours}}시간', {
                hours: overTimeHistoryData?.remainingHours ?? '-',
              })}
            </Typography>
          </Box>
          <Box
            sx={{
              width: '1px',
              backgroundColor: 'var(--dk-bg-strong, #E7E9EC)',
              alignSelf: 'stretch',
            }}
          />
          <Box
            sx={{
              flex: '1 0 0',
              padding: '12px 4px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <Typography
              sx={{
                color: 'var(--dk-text-secondary, #888D96)',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
                textAlign: 'center',
              }}
            >
              {t('MYPAGE.2주 내 소멸 예정')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                justifyContent: 'center',
              }}
            >
              <AccessTimeIcon
                sx={{ fontSize: '20px', color: 'var(--dk-danger, #F55060)' }}
              />
              <Typography
                sx={{
                  color: 'var(--dk-danger, #F55060)',
                  fontSize: '16px',
                  fontWeight: 500,
                  lineHeight: '24px',
                }}
              >
                {t('MYPAGE.{{hours}}시간', {
                  hours: overTimeHistoryData?.expiringHours ?? '-',
                })}
              </Typography>
            </Box>
          </Box>
        </Box>

        <OvertimeTable>
          <thead>
            <tr>
              <th className="col-type">{t('MYPAGE.구분')}</th>
              <th className="col-date">{t('MYPAGE.신청일')}</th>
              <th className="col-expiry">{t('MYPAGE.소멸 예정일')}</th>
              <th className="col-hours">{t('MYPAGE.시간')}</th>
              <th className="col-kebab" />
            </tr>
          </thead>
          <tbody>
            {histories.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  style={{
                    textAlign: 'center',
                    padding: '16px 0',
                    color: 'var(--dk-text-secondary, #888D96)',
                  }}
                >
                  {t('MYPAGE.내역이 없습니다.')}
                </td>
              </tr>
            ) : (
              visibleHistories.map((item, i) => {
                const isPlus = item.recordType === 'OVERTIME';
                // 케밥 팝오버가 열린 행의 아이콘 버튼만 바탕색 강조
                const isKebabOpen =
                  Boolean(overtimeMenuAnchor) &&
                  overtimeMenuItem?.approvalId === item.approvalId;
                return (
                  <tr key={i}>
                    <td className={`col-type ${isPlus ? 'plus' : 'minus'}`}>
                      {item.typeLabel}
                    </td>
                    <td
                      className={`col-date${item.approvalId ? ' link' : ''}`}
                      onClick={
                        item.approvalId
                          ? () =>
                              handleApprovalNavigation(
                                // RN WebView 는 intranet 문서 뷰 URL 로 MOVE_TAB
                                window?.isRNWebview
                                  ? getIntranetDocUrl(item.approvalId, false)
                                  : getApprovalDocUrl(item.approvalId, false),
                              )
                          : undefined
                      }
                    >
                      {formatMonthDay(item.date)}
                    </td>
                    <td className="col-expiry">
                      {item.expiringAt ? formatMonthDay(item.expiringAt) : ''}
                    </td>
                    <td className={`col-hours ${isPlus ? 'plus' : 'minus'}`}>
                      {isPlus ? '+' : '-'}
                      {t('MYPAGE.{{hours}}시간', { hours: item.hours })}
                    </td>
                    <td className="col-kebab">
                      {item.approvalId && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleOvertimeKebabOpen(e, item)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '20px',
                            height: '20px',
                            margin: '0 auto',
                            padding: 0,
                            borderRadius: '4px',
                            backgroundColor: isKebabOpen
                              ? 'var(--dk-bg-muted, #F3F5F6)'
                              : 'var(--dk-bg, #FFF)',
                            '&:hover': {
                              backgroundColor: 'var(--dk-bg-muted, #F3F5F6)',
                            },
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </td>
                  </tr>
                );
              })
            )}

            {/* 더보기/접기 — 테이블 내부 마지막 행 */}
            {hasMore && (
              <tr>
                <td className="col-more" colSpan={5}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      padding: '12px 0',
                      cursor: 'pointer',
                    }}
                    onClick={() => setIsOvertimeExpanded((prev) => !prev)}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '20px',
                      }}
                    >
                      {isOvertimeExpanded
                        ? t('MYPAGE.접기')
                        : t('MYPAGE.더보기')}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: '16px',
                        height: '16px',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M4 6L8 10L12 6' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        transform: isOvertimeExpanded
                          ? 'rotate(180deg)'
                          : 'none',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </Box>
                </td>
              </tr>
            )}
          </tbody>
        </OvertimeTable>

        <BoardDetailMenu
          anchorEl={overtimeMenuAnchor}
          open={Boolean(overtimeMenuAnchor)}
          onClose={handleOvertimeKebabClose}
          // BoardDetailMenu 기본값(width 104px / padding 6px 10px) 을 이 화면에서만 재정의
          sx={{
            '& .MuiMenu-list': {
              width: 'auto',
              minWidth: '160px',
              padding: '8px',
            },
            // Tooltip 의 span 래퍼 때문에 '> li' 로는 닿지 않아 클래스로 지정
            '& .MuiMenuItem-root': {
              padding: '8px 12px',
            },
          }}
        >
          {(() => {
            // cancelType==='none' → 취소 불가(딤드). 미선택 시도 disabled 처리.
            const isDisabled =
              !overtimeMenuItem || overtimeMenuItem.cancelType === 'none';
            return (
              <Tooltip
                title={
                  isDisabled
                    ? // 문구의 ':' 가 i18next ns 구분자로 해석되는 것 방지 (nsSeparator:false)
                      t('MYPAGE.이미 정산/소멸된 항목입니다. 문의: HR팀', {
                        nsSeparator: false,
                      })
                    : ''
                }
                placement="top"
              >
                <span style={{ display: 'block' }}>
                  <MenuItem
                    disabled={isDisabled}
                    style={{ color: isDisabled ? '#ccc' : 'red' }}
                    sx={{
                      '& .MuiTouchRipple-root': {
                        color: 'var(--dk-text, black)',
                      },
                    }}
                    onClick={handleOvertimeCancelRequest}
                  >
                    {t('MYPAGE.취소하기')}
                  </MenuItem>
                </span>
              </Tooltip>
            );
          })()}
        </BoardDetailMenu>
      </Section>

      {/* 연장 근무 신청 버튼 */}
      <Section sx={{ padding: '16px 20px 20px' }}>
        <Stack gap={'8px'}>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            onClick={() => setIsRequestModalOpen(true)}
            sx={{
              borderColor: 'var(--dk-line, #E7E9EC)',
              color: 'var(--dk-text, #000)',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '24px',
              borderRadius: '8px',
              height: '48px',
            }}
          >
            {t('MYPAGE.연장근무 신청')}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            onClick={() => setIsAdjustModalOpen(true)}
            sx={{
              borderColor: 'var(--dk-line, #E7E9EC)',
              color: 'var(--dk-text, #000)',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '24px',
              borderRadius: '8px',
              height: '48px',
            }}
          >
            {t('MYPAGE.연장근무 적립 사용 신청')}
          </Button>
        </Stack>
      </Section>

      <OvertimeAdjustModal
        open={isAdjustModalOpen}
        onClose={() => setIsAdjustModalOpen(false)}
        workingHours={myInfo.workingHours}
        onSuccess={mutateOverTimeHistory}
      />
      <OvertimeRequestModal
        open={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        workingHours={myInfo.workingHours}
        onSuccess={mutateOverTimeHistory}
      />
    </>
  );
};

export default Overtime;
